/**
 * v3.21-1709 — Standalone Closing Packet workspace.
 * URL: /admin/closing-packet/:client_id
 * Fetches Client + Deal + Ownerships then mounts <ClosingPacketBuilder/>.
 * Admin-gated via PhoneSystemAdminRoute in App.js.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import ClosingPacketBuilder from '../components/ClosingPacketBuilder';
import { FileText, ArrowLeft } from 'lucide-react';

export default function ClosingPacketWorkspacePage() {
  const { client_id } = useParams();
  const [client, setClient] = useState(null);
  const [deal, setDeal] = useState(null);
  const [ownerships, setOwnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: c }, { data: o }] = await Promise.all([
          api.get(`/clients/${client_id}`),
          api.get(`/clients/${client_id}/ownerships`).catch(() => ({ data: { items: [] } })),
        ]);
        setClient(c?.client || c);
        setOwnerships(o?.items || []);
        // Deal: pick the client's most recent active deal (best-effort)
        try {
          const { data: dl } = await api.get(`/clients/${client_id}/deals`);
          const list = Array.isArray(dl) ? dl : (dl?.items || dl?.deals || []);
          setDeal(list?.[0] || null);
        } catch { setDeal(null); }
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load');
      } finally { setLoading(false); }
    })();
  }, [client_id]);

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">{error}</div>;
  if (!client) return <div className="p-6 text-sm text-slate-500">Client not found</div>;

  return (
    <div className="p-4 sm:p-6 max-w-5xl min-w-0" data-testid="closing-packet-workspace">
      <div className="mb-4 flex items-center gap-2">
        <Link to={`/clients/${client_id}`}
              className="text-xs text-slate-500 hover:text-indigo-600 flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to Client
        </Link>
      </div>
      <div className="mb-4 flex items-start gap-3">
        <FileText className="h-6 w-6 text-indigo-600 mt-0.5" />
        <div>
          <h1 className="text-lg font-semibold text-slate-800">Ghost Deal Closing Packet</h1>
          <p className="text-xs text-slate-500">
            PTC Complete Closing Packet · Signed via Adobe Acrobat Sign (PTC Secure Sign)
          </p>
        </div>
      </div>
      <ClosingPacketBuilder client={client} deal={deal} ownerships={ownerships} />
    </div>
  );
}
